import Table from "../../components/RecordTable";
import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  message,
  Popconfirm,
  Tag,
  Card,
  Row,
  Col,
  Statistic,
  Divider,
  InputNumber,
  Switch,
  List,
  Typography,
  Badge,
  Spin,
  Alert,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DollarOutlined,
  ExperimentOutlined,
  HomeOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { getSession } from "../../services/session";
import API, { doctorSettingsAPI, userAPI } from "../../services/api";

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

const SAMPLE_TYPES = ["blood", "urine", "stool", "saliva", "tissue", "other"];

const LabTests = () => {
  const [form] = Form.useForm();
  const { labId: routeLabId } = useParams();
  const [selectedLabAdmin, setSelectedLabAdmin] = useState(null);
  const [labAdmins, setLabAdmins] = useState([]);
  const sessionUser = getSession()?.user;
  const ownLabAdmin = sessionUser?.role === "labadmin" ? sessionUser._id : null;
  const labId = routeLabId || ownLabAdmin || selectedLabAdmin;
  useEffect(() => {
    if (!routeLabId && !ownLabAdmin) userAPI.getAll({ role: "labadmin", limit: 100 }).then(res => setLabAdmins(res.data?.users || [])).catch(() => message.error("Unable to load laboratory administrators"));
  }, [routeLabId, ownLabAdmin]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [tests, setTests] = useState([]);
  const [labPartners, setLabPartners] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editingTest, setEditingTest] = useState(null);
  const [selectedPricing, setSelectedPricing] = useState([]);
  const [stats, setStats] = useState({});

  useEffect(() => {
    fetchTests();
    fetchCategories();
    if (labId) {
      fetchLabPartners();
      fetchCategories();
      fetchStats();
    }
  }, [labId]);

  const fetchTests = async () => {
    try {
      setLoading(true);
      const response = await (labId ? API.labTest.getByLabAdmin(labId) : API.labTest.getAll());
      setTests(response.data || []);
    } catch (error) {
      console.error("Fetch tests error:", error);
      message.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to fetch lab tests"
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchLabPartners = async () => {
    try {
      const response = await API.labPartner.getAll(labId);
      setLabPartners(response?.data || []);
    } catch (error) {
      console.error("Fetch lab partners error:", error);
      message.error(
        error.response?.data?.message || "Failed to fetch lab partners"
      );
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await doctorSettingsAPI.getSettings();
      setCategories(response.data?.labTestCategories || []);
    } catch (error) {
      console.error("Fetch categories error:", error);
      setCategories([]);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await API.labTest.getStats(labId);
      setStats(response.data || {});
    } catch (error) {
      console.error("Fetch stats error:", error);
    }
  };

  const handleOpenModal = (test = null) => {

    if (test) {
      setEditingTest(test);

      // Ensure category is always an array
      const categoryValue = Array.isArray(test.category)
        ? test.category
        : test.category
        ? [test.category]
        : [];

      form.setFieldsValue({
        name: test.name,
        code: test.code,
        category: categoryValue,
        description: test.description,
        sampleType: test.sampleType,
        reportDeliveryTime: test.reportDeliveryTime,
        preparationRequired: test.preparationRequired,
        fastingRequired: test.fastingRequired,
        homeCollectionAvailable: test.homeCollectionAvailable,
        homeCollectionCharge: test.homeCollectionCharge,
      });

      // Map existing pricing to all lab partners
      const pricingMap = {};
      if (test.labPricing) {
        test.labPricing.forEach((p) => {
          const partnerId = p.labPartner?._id || p.labPartner;
          pricingMap[partnerId] = {
            price: p.price,
            discountedPrice: p.discountedPrice,
            isAvailable: p.isAvailable,
            notes: p.notes || "",
          };
        });
      }

      // Create pricing array for all lab partners
      const pricing = labPartners.map((partner) => ({
        labPartner: partner._id,
        price: pricingMap[partner._id]?.price || 0,
        discountedPrice: pricingMap[partner._id]?.discountedPrice || 0,
        isAvailable:
          pricingMap[partner._id]?.isAvailable !== undefined
            ? pricingMap[partner._id].isAvailable
            : true,
        notes: pricingMap[partner._id]?.notes || "",
      }));

      setSelectedPricing(pricing);
    } else {
      setEditingTest(null);
      form.resetFields();

      // Initialize pricing for all lab partners with default values
      const initialPricing = labPartners.map((partner) => ({
        labPartner: partner._id,
        price: 0,
        discountedPrice: 0,
        isAvailable: true,
        notes: "",
      }));

      setSelectedPricing(initialPricing);
    }

    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setModalVisible(false);
    setEditingTest(null);
    form.resetFields();
    setSelectedPricing([]);
  };

  const handlePricingChange = (labPartnerId, field, value) => {
    setSelectedPricing((prev) => {
      const updated = prev.map((p) =>
        p.labPartner === labPartnerId ? { ...p, [field]: value } : p
      );
      return updated;
    });
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      // Ensure category is an array
      const categoryArray = Array.isArray(values.category)
        ? values.category
        : values.category
        ? [values.category]
        : [];

      // Filter out pricing with price = 0 or undefined
      const validPricing = selectedPricing.filter(
        (p) => p.price && p.price > 0
      );

      if (validPricing.length === 0) {
        message.warning("Please add at least one lab partner pricing");
        setLoading(false);
        return;
      }

      const testData = {
        ...values,
        category: categoryArray,
        labPricing: validPricing,
        labAdminId: labId,
        createdBy: labId,
      };


      if (editingTest) {
        const response = await API.labTest.update(editingTest._id, testData);
        message.success("Lab test updated successfully");
      } else {
        const response = await API.labTest.create(testData);
        message.success("Lab test created successfully");
      }

      handleCloseModal();
      await fetchTests();
      await fetchStats();
      await fetchCategories(); // Refresh categories
    } catch (error) {
      console.error("Submit error:", error);
      console.error("Error response:", error.response);
      message.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to save lab test"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await API.labTest.delete(id);
      message.success("Lab test deleted successfully");
      await fetchTests();
      await fetchStats();
    } catch (error) {
      console.error("Delete error:", error);
      message.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to delete lab test"
      );
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "Test Name",
      dataIndex: "name",
      key: "name",
      fixed: "left",
      width: 200,
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: 600, color: "#0A6EBD" }}>{text}</div>
          {record.code && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Code: {record.code}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: "Categories",
      dataIndex: "category",
      key: "category",
      width: 180,
      render: (categories) => {
        const categoryArray = Array.isArray(categories)
          ? categories
          : categories
          ? [categories]
          : [];
        return (
          <Space size={[0, 4]} wrap>
            {categoryArray.length > 0 ? (
              categoryArray.map((cat, index) => (
                <Tag key={index} color="blue">
                  {cat}
                </Tag>
              ))
            ) : (
              <Text type="secondary">No category</Text>
            )}
          </Space>
        );
      },
    },
    {
      title: "Sample Type",
      dataIndex: "sampleType",
      key: "sampleType",
      width: 120,
      render: (type) => <Tag color="purple">{type}</Tag>,
    },
    {
      title: "Price Range",
      key: "priceRange",
      width: 150,
      render: (_, record) => {
        if (record.labPricing && record.labPricing.length > 0) {
          const prices = record.labPricing
            .filter((p) => p.isAvailable)
            .map((p) => p.discountedPrice || p.price);
          if (prices.length > 0) {
            const min = Math.min(...prices);
            const max = Math.max(...prices);
            return (
              <Text strong>
                ₹{min} {min !== max && `- ₹${max}`}
              </Text>
            );
          }
        }
        return <Text type="secondary">No pricing</Text>;
      },
    },
    {
      title: "Lab Partners",
      key: "labPartners",
      width: 120,
      render: (_, record) => (
        <Badge
          count={record.labPricing?.length || 0}
          style={{ backgroundColor: "#0A6EBD" }}
        >
          <Tag icon={<DollarOutlined />} color="cyan">
            Pricing
          </Tag>
        </Badge>
      ),
    },
    {
      title: "Report Time",
      dataIndex: "reportDeliveryTime",
      key: "reportDeliveryTime",
      width: 120,
      render: (time) => (
        <Tag icon={<ClockCircleOutlined />} color="orange">
          {time}
        </Tag>
      ),
    },
    {
      title: "Home Collection",
      dataIndex: "homeCollectionAvailable",
      key: "homeCollectionAvailable",
      width: 130,
      render: (available, record) =>
        available ? (
          <Tag icon={<HomeOutlined />} color="green">
            ₹{record.homeCollectionCharge || 0}
          </Tag>
        ) : (
          <Tag color="default">Not Available</Tag>
        ),
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      width: 100,
      render: (active) =>
        active ? (
          <Tag icon={<CheckCircleOutlined />} color="success">
            Active
          </Tag>
        ) : (
          <Tag color="error">Inactive</Tag>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            ghost
            size="small"
            icon={<EditOutlined />}
            disabled={!labId}
            onClick={() => handleOpenModal(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete Lab Test"
            description="Are you sure you want to delete this test?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button danger size="small" icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];


  return (
    <div>
      {/* Statistics Cards */}
      {labId && <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Tests"
              value={stats.totalTests || 0}
              prefix={<ExperimentOutlined />}
              styles={{ content: { color: "#0A6EBD" } }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Active Tests"
              value={stats.activeTests || 0}
              prefix={<CheckCircleOutlined />}
              styles={{ content: { color: "#0A6EBD" } }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Categories"
              value={stats.totalCategories || 0}
              styles={{ content: { color: "#0A6EBD" } }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Home Collection"
              value={stats.homeCollectionTests || 0}
              prefix={<HomeOutlined />}
              styles={{ content: { color: "#faad14" } }}
            />
          </Card>
        </Col>
      </Row>}

      {/* Header with Add Button */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <div>
          <h2 style={{ margin: 0, color: "#0A6EBD" }}>Test catalogue</h2>
          {!routeLabId && !ownLabAdmin && <Select aria-label="Laboratory administrator" placeholder="Select a laboratory administrator to manage tests" style={{ width: "min(420px, 100%)", marginTop: 12 }} value={selectedLabAdmin} onChange={setSelectedLabAdmin} options={labAdmins.map(admin => ({ value: admin._id, label: admin.name }))} />}
          <p style={{ margin: 0, color: "#666" }}>
            Manage your lab tests and pricing
          </p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          disabled={!labId}
          onClick={() => {
            handleOpenModal();
          }}
          size="large"
          style={{
            borderRadius: 8,
          }}
        >
          Add Lab Test
        </Button>
      </div>

      {!labId && <Alert type="info" showIcon style={{ marginBottom: 16 }} message="Select a laboratory administrator above to add or edit tests." description={labAdmins.length ? "Tests and prices belong to a laboratory. Choose the administrator responsible for this catalogue." : "Create a laboratory administrator in Users, then return here to assign tests and prices."} />}
      {/* Warning if no lab partners */}
      {labId && labPartners.length === 0 && (
        <Card
          style={{
            marginBottom: 24,
            backgroundColor: "#fff7e6",
            borderColor: "#ffa940",
          }}
        >
          <Space>
            <ExperimentOutlined style={{ fontSize: 20, color: "#0A6EBD" }} />
            <div>
              <Text strong>No Lab Partners Found</Text>
              <br />
              <Text type="secondary">
                Please add lab partners first before creating lab tests.
              </Text>
            </div>
          </Space>
        </Card>
      )}

      {/* Tests Table */}
      <Table
        columns={columns}
        dataSource={tests}
        rowKey="_id"
        loading={loading}
        // scroll={{ x: 1500 }}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} lab tests`,
        }}
        bordered
        style={{
          borderRadius: 12,
          overflow: "hidden",
        }}
        scroll={{ x: "max-content" }}
        components={{
          body: {
            wrapper: (props) => (
              <tbody {...props} style={{ transition: "none" }} />
            ),
          },
        }}
      />

      {/* Add/Edit Modal */}
      <Modal
        title={
          <div style={{ fontSize: 20, fontWeight: 600, color: "#0A6EBD" }}>
            {editingTest ? "Edit Lab Test" : "Add Lab Test"}
          </div>
        }
        open={modalVisible}
        onCancel={handleCloseModal}
        footer={null}
        width={900}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Test Name"
                name="name"
                rules={[{ required: true, message: "Please enter test name" }]}
              >
                <Input
                  size="large"
                  placeholder="Enter test name"
                  prefix={<ExperimentOutlined style={{ color: "#0A6EBD" }} />}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label="Test Code" name="code">
                <Input size="large" placeholder="Enter test code (optional)" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Categories (Optional - Select multiple)"
                name="category"
              >
                <Select
                  size="large"
                  placeholder={categories.length > 0 ? "Select categories" : "No categories — add in Doctor Settings"}
                  mode="multiple"
                  allowClear
                  maxTagCount="responsive"
                  disabled={categories.length === 0}
                >
                  {categories.map((cat) => (
                    <Option key={cat} value={cat}>
                      {cat}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Sample Type"
                name="sampleType"
                rules={[
                  { required: true, message: "Please select sample type" },
                ]}
              >
                <Select size="large" placeholder="Select sample type">
                  {SAMPLE_TYPES.map((type) => (
                    <Option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Description" name="description">
            <TextArea
              rows={3}
              placeholder="Enter test description"
              showCount
              maxLength={500}
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Report Delivery Time"
                name="reportDeliveryTime"
                rules={[
                  {
                    required: true,
                    message: "Please enter report delivery time",
                  },
                ]}
              >
                <Input size="large" placeholder="e.g., 24 hours, 2-3 days" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Preparation Required"
                name="preparationRequired"
              >
                <Input size="large" placeholder="e.g., Fasting for 8 hours" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Fasting Required"
                name="fastingRequired"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                label="Home Collection"
                name="homeCollectionAvailable"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item label="Collection Charge" name="homeCollectionCharge">
                <InputNumber
                  size="large"
                  style={{ width: "100%" }}
                  min={0}
                  prefix="₹"
                  placeholder="0"
                />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">
            <Text strong style={{ color: "#0A6EBD" }}>
              Lab Partner Pricing (Add price for at least one partner)
            </Text>
          </Divider>

          <div
            style={{
              maxHeight: 300,
              overflowY: "auto",
              padding: "8px",
              border: "1px solid #f0f0f0",
              borderRadius: 8,
              backgroundColor: "#fafafa",
            }}
          >
            {labPartners.length > 0 ? (
              <List
                dataSource={labPartners}
                renderItem={(partner) => {
                  const pricing = selectedPricing.find(
                    (p) => p.labPartner === partner._id
                  );
                  return (
                    <List.Item
                      key={partner._id}
                      style={{
                        backgroundColor: "white",
                        marginBottom: 8,
                        padding: 12,
                        borderRadius: 8,
                      }}
                    >
                      <div style={{ width: "100%" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            flexWrap: "wrap",
                          }}
                        >
                          <div style={{ flex: 1, minWidth: 150 }}>
                            <Text strong>{partner.labName}</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {partner.locations?.length || 0} locations
                            </Text>
                          </div>

                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              alignItems: "center",
                            }}
                          >
                            <InputNumber
                              size="middle"
                              placeholder="Price"
                              prefix="₹"
                              min={0}
                              value={pricing?.price}
                              onChange={(value) =>
                                handlePricingChange(partner._id, "price", value)
                              }
                              style={{ width: 120 }}
                            />

                            <InputNumber
                              size="middle"
                              placeholder="Discount"
                              prefix="₹"
                              min={0}
                              max={pricing?.price || 0}
                              value={pricing?.discountedPrice}
                              onChange={(value) =>
                                handlePricingChange(
                                  partner._id,
                                  "discountedPrice",
                                  value
                                )
                              }
                              style={{ width: 120 }}
                              disabled={!pricing?.price || pricing?.price === 0}
                            />

                            <Switch
                              checked={pricing?.isAvailable}
                              onChange={(checked) =>
                                handlePricingChange(
                                  partner._id,
                                  "isAvailable",
                                  checked
                                )
                              }
                              checkedChildren="Available"
                              unCheckedChildren="Unavailable"
                            />
                          </div>
                        </div>
                      </div>
                    </List.Item>
                  );
                }}
              />
            ) : (
              <div style={{ textAlign: "center", padding: 40 }}>
                <Text type="secondary">
                  No lab partners available. Please add lab partners first.
                </Text>
              </div>
            )}
          </div>

          <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
            <Space style={{ width: "100%", justifyContent: "flex-end" }}>
              <Button onClick={handleCloseModal} size="large">
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                size="large"
                style={{
                  background: "linear-gradient(135deg, #0A6EBD, #0A6EBD)",
                  border: "none",
                }}
              >
                {editingTest ? "Update Test" : "Create Test"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LabTests;
